/**
 * Clients view.
 *
 * Lists the claimed player clients for an event and lets a gamemaster/admin
 * kick (forcibly disconnect) one. Access is gated by role in addition to the
 * backend authorization.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserSlash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../../store';
import { fetchClients, kickClient, kickAllClients, resetClients } from '../../store/clientsSlice';
import { Client } from '../../types/clients';
import ConfirmDeleteModal from '../orbat/ConfirmDeleteModal';
import './ClientsView.scss';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Parse a backend timestamp as UTC. The API serializes naive UTC datetimes
 * with no timezone designator (`last_used_at` is a naive `DateTime` column);
 * without appending 'Z', `new Date` would interpret the string in the viewer's
 * local zone and skew every "last seen" value by the UTC offset.
 */
function parseUtcDate(iso: string): Date {
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
    return new Date(hasTz ? iso : `${iso}Z`);
}

/**
 * Humanize a "last seen" ISO timestamp into a short relative string, falling
 * back to a locale date for older values. Returns "Never" for null.
 */
function formatLastSeen(iso: string | null): string {
    if (!iso) return 'Never';
    const date = parseUtcDate(iso);
    if (Number.isNaN(date.getTime())) return 'Unknown';

    const diffMs = Date.now() - date.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 45) return 'Just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * A revoked/inactive client may still hold a live MQTT session that missed the
 * (QoS 1, non-retained, clean-session) kick nudge, so keep a re-kick action
 * available while it was seen recently. Mirrors the backend's 1h idle TTL,
 * after which the credential is considered gone and the unit re-claimable.
 */
const RECENT_SEEN_MS = 60 * 60 * 1000;

function isRecentlySeen(iso: string | null): boolean {
    if (!iso) return false;
    const t = parseUtcDate(iso).getTime();
    return !Number.isNaN(t) && Date.now() - t < RECENT_SEEN_MS;
}

// =============================================================================
// Component
// =============================================================================

const ClientsView: React.FC = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const dispatch = useDispatch<AppDispatch>();

    const { clients, isLoading, error, kickingIds, isKickingAll } = useSelector(
        (state: RootState) => state.clients
    );
    const userRole = useSelector((state: RootState) => state.auth.user?.role);
    const isGamemasterOrAdmin = userRole === 'gamemaster' || userRole === 'admin';

    // Client pending kick confirmation (local UI state).
    const [clientToKick, setClientToKick] = useState<Client | null>(null);
    // Whether the "kick all" confirmation is open.
    const [showKickAll, setShowKickAll] = useState(false);

    // Fetch clients on mount / when the event changes (gamemaster/admin only).
    // Reset on cleanup so switching events never shows the previous event's
    // clients (with live Kick buttons keyed to the wrong event) during refetch.
    useEffect(() => {
        if (eventId && isGamemasterOrAdmin) {
            dispatch(fetchClients({ eventId }));
        }
        return () => {
            dispatch(resetClients());
        };
    }, [eventId, isGamemasterOrAdmin, dispatch]);

    const handleKickClick = useCallback((client: Client) => {
        setClientToKick(client);
    }, []);

    const handleCancelKick = useCallback(() => {
        setClientToKick(null);
    }, []);

    const handleConfirmKick = useCallback(async () => {
        if (!eventId || !clientToKick) return;
        try {
            await dispatch(
                kickClient({ eventId, unitId: clientToKick.unit_id })
            ).unwrap();
            // The kickClient.fulfilled reducer optimistically marks the row
            // inactive, so no refetch is needed here.
        } catch {
            // Error is surfaced via the redux `error` state and the Alert below.
        } finally {
            setClientToKick(null);
        }
    }, [eventId, clientToKick, dispatch]);

    const handleConfirmKickAll = useCallback(async () => {
        if (!eventId) return;
        try {
            await dispatch(kickAllClients({ eventId })).unwrap();
            // kickAllClients.fulfilled optimistically marks every row inactive.
        } catch {
            // Error is surfaced via the redux `error` state and the Alert below.
        } finally {
            setShowKickAll(false);
        }
    }, [eventId, dispatch]);

    // Role gate — mirror the nav gating so a direct navigation is also blocked.
    if (!isGamemasterOrAdmin) {
        return (
            <Container fluid className="h-100 d-flex align-items-center justify-content-center">
                <Row>
                    <Col className="text-center text-muted">
                        <FontAwesomeIcon icon={faUserSlash} size="4x" className="mb-3" />
                        <h3>Clients</h3>
                        <p>Only a gamemaster or admin can manage clients.</p>
                    </Col>
                </Row>
            </Container>
        );
    }

    const isKicking = clientToKick
        ? kickingIds.includes(clientToKick.unit_id)
        : false;

    // Clients that can be (re-)kicked: active, or revoked but seen recently.
    const kickableCount = clients.filter(
        (c) => c.is_active || isRecentlySeen(c.last_used_at)
    ).length;

    // Loading state (initial fetch only).
    if (isLoading && clients.length === 0) {
        return (
            <Container fluid className="h-100 d-flex align-items-center justify-content-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading clients...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <div className="clients-view">
            <div className="clients-view__header">
                <h3>
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Clients
                </h3>
                <p>Claimed player devices for this event. Kick a client to disconnect it.</p>
            </div>

            {error && (
                <Alert variant="danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
            )}

            {kickableCount > 0 && (
                <div className="d-flex justify-content-end mb-3">
                    <Button
                        variant="danger"
                        onClick={() => setShowKickAll(true)}
                        disabled={isKickingAll}
                        title="Disconnect all clients in this event"
                    >
                        {isKickingAll ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Kicking all...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faUserSlash} className="me-2" />
                                Kick all ({kickableCount})
                            </>
                        )}
                    </Button>
                </div>
            )}

            {clients.length === 0 ? (
                <div className="clients-view__empty">
                    <FontAwesomeIcon icon={faUsers} size="4x" />
                    <h4>No clients yet</h4>
                    <p>Player devices appear here once they claim a unit for this event.</p>
                </div>
            ) : (
                <Table hover responsive className="clients-view__table">
                    <thead>
                        <tr>
                            <th>Unit</th>
                            <th>Faction</th>
                            <th>Last Seen</th>
                            <th>Status</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.map((client) => {
                            const rowKicking = kickingIds.includes(client.unit_id);
                            // Allow (re-)kicking active clients, and revoked ones
                            // seen recently — they may still hold a live session
                            // that missed the first nudge (finding #4).
                            const canKick =
                                client.is_active || isRecentlySeen(client.last_used_at);
                            const kickLabel = client.is_active ? 'Kick' : 'Re-kick';
                            return (
                                <tr key={client.client_id}>
                                    <td>{client.unit_name}</td>
                                    <td>{client.faction_name}</td>
                                    <td className="clients-view__last-seen">
                                        {formatLastSeen(client.last_used_at)}
                                    </td>
                                    <td>
                                        {client.is_active ? (
                                            <Badge bg="success">Active</Badge>
                                        ) : (
                                            <Badge bg="secondary">Inactive</Badge>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {canKick && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleKickClick(client)}
                                                disabled={rowKicking}
                                                title={`${kickLabel} ${client.unit_name}`}
                                            >
                                                {rowKicking ? (
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon
                                                            icon={faUserSlash}
                                                            className="me-1"
                                                        />
                                                        {kickLabel}
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            {/* Kick confirmation modal (reuses the shared destructive-action modal). */}
            <ConfirmDeleteModal
                show={!!clientToKick}
                onHide={handleCancelKick}
                onConfirm={handleConfirmKick}
                title="Kick player?"
                message={`Are you sure you want to kick "${clientToKick?.unit_name}"? Their app will be disconnected immediately.`}
                confirmLabel="Kick"
                loadingLabel="Kicking"
                isLoading={isKicking}
            />

            {/* Bulk "kick all" confirmation. */}
            <ConfirmDeleteModal
                show={showKickAll}
                onHide={() => setShowKickAll(false)}
                onConfirm={handleConfirmKickAll}
                title="Kick all players?"
                message={`Disconnect all ${kickableCount} client${kickableCount === 1 ? '' : 's'} from this event? Their apps will return to the lobby — players can re-join from the lobby.`}
                confirmLabel="Kick all"
                loadingLabel="Kicking all"
                isLoading={isKickingAll}
            />
        </div>
    );
};

export default ClientsView;
